import { cagr, roundToDecimal } from "../utils/math";

export interface CompoundInterestResult {
  endBalance: number;
  totalContributions: number;
  totalInterest: number;
  yearlyBreakdown: Array<{
    year: number;
    contributions: number;
    interest: number;
    balance: number;
  }>;
}

function calculateContinuousCompoundingYear(
  startBalance: number,
  rateDecimal: number,
  monthlyContribution: number,
  contributionTiming: "beginning" | "end"
): { balance: number; yearContributions: number } {
  let balance = startBalance;
  let yearContributions = 0;
  for (let m = 0; m < 12; m++) {
    if (contributionTiming === "beginning") {
      balance += monthlyContribution;
      yearContributions += monthlyContribution;
    }
    
    // Continuous compounding for 1 month (1/12 of a year)
    balance = balance * Math.exp(rateDecimal * (1 / 12));
    
    if (contributionTiming === "end") {
      balance += monthlyContribution;
      yearContributions += monthlyContribution;
    }
  }
  return { balance, yearContributions };
}

function calculateDiscreteCompoundingYear(
  startBalance: number,
  rateDecimal: number,
  compoundingFrequency: number,
  monthlyContribution: number,
  contributionTiming: "beginning" | "end"
): { balance: number; yearContributions: number } {
  let balance = startBalance;
  let yearContributions = 0;
  const f = compoundingFrequency;
  const ratePerPeriod = rateDecimal / f;
  const periodsPerYear = f;
  
  // Rate of interest applied monthly: (1 + r/f)^(f/12) - 1
  const monthlyEquivalentRate = Math.pow(1 + ratePerPeriod, periodsPerYear / 12) - 1;

  for (let m = 1; m <= 12; m++) {
    if (contributionTiming === "beginning") {
      balance += monthlyContribution;
      yearContributions += monthlyContribution;
    }
    
    balance = balance * (1 + monthlyEquivalentRate);

    if (contributionTiming === "end") {
      balance += monthlyContribution;
      yearContributions += monthlyContribution;
    }
  }
  return { balance, yearContributions };
}

/**
 * Calculates compound interest with contributions
 */
export function compoundInterest(
  principal: number,
  annualRate: number,
  years: number,
  compoundingFrequency = 12, // default monthly compounding
  monthlyContribution = 0,
  contributionTiming: "beginning" | "end" = "end"
): CompoundInterestResult {
  const rateDecimal = annualRate / 100;
  const isContinuous = compoundingFrequency === -1;
  
  let balance = principal;
  let totalContributions = principal;
  const yearlyBreakdown: CompoundInterestResult["yearlyBreakdown"] = [];

  for (let year = 1; year <= years; year++) {
    const result = isContinuous
      ? calculateContinuousCompoundingYear(balance, rateDecimal, monthlyContribution, contributionTiming)
      : calculateDiscreteCompoundingYear(balance, rateDecimal, compoundingFrequency, monthlyContribution, contributionTiming);

    balance = result.balance;
    totalContributions += result.yearContributions;

    yearlyBreakdown.push({
      year,
      contributions: roundToDecimal(totalContributions, 2),
      interest: roundToDecimal(balance - totalContributions, 2),
      balance: roundToDecimal(balance, 2),
    });
  }

  return {
    endBalance: roundToDecimal(balance, 2),
    totalContributions: roundToDecimal(totalContributions, 2),
    totalInterest: roundToDecimal(balance - totalContributions, 2),
    yearlyBreakdown,
  };
}

/**
 * SIP (Systematic Investment Plan) Future Value
 */
export function sipCalculator(
  monthlyContribution: number,
  annualRate: number,
  years: number
): CompoundInterestResult {
  return compoundInterest(0, annualRate, years, 12, monthlyContribution, "end");
}

/**
 * Lumpsum investment growth
 */
export function lumpsumGrowth(
  principal: number,
  annualRate: number,
  years: number
): CompoundInterestResult {
  return compoundInterest(principal, annualRate, years, 12, 0, "end");
}

/**
 * Compare SIP vs Lumpsum investment for the same total capital
 */
export function sipVsLumpsum(
  totalInvestment: number,
  annualRate: number,
  years: number
): { sipResult: CompoundInterestResult; lumpsumResult: CompoundInterestResult } {
  // Lumpsum deposits everything on Day 1
  const lumpsumResult = lumpsumGrowth(totalInvestment, annualRate, years);
  
  // SIP deposits totalInvestment divided into monthly slices over the years
  const monthlyContribution = years > 0 ? totalInvestment / (years * 12) : 0;
  const sipResult = sipCalculator(monthlyContribution, annualRate, years);

  return { sipResult, lumpsumResult };
}

export interface DividendResult {
  endBalance: number;
  totalDividendsPaid: number;
  yearlyBreakdown: Array<{
    year: number;
    shares: number;
    sharePrice: number;
    dividends: number;
    balance: number;
  }>;
}

/**
 * Dividend Reinvestment (DRIP) and Growth model
 */
export function dividendGrowth(
  initialShares: number,
  initialSharePrice: number,
  initialDividendYield: number, // percentage, e.g. 3.5 for 3.5%
  dividendGrowthRate: number, // percentage, e.g. 5 for 5% annual dividend growth
  sharePriceGrowthRate: number, // percentage, e.g. 7 for 7% annual price growth
  years: number,
  reinvestDividends = true
): DividendResult {
  let shares = initialShares;
  let sharePrice = initialSharePrice;
  let dividendPerShare = (initialSharePrice * (initialDividendYield / 100));
  let totalDividendsPaid = 0;
  const yearlyBreakdown: DividendResult["yearlyBreakdown"] = [];

  for (let year = 1; year <= years; year++) {
    // End of year dividend payout
    const dividendsPaid = shares * dividendPerShare;
    totalDividendsPaid += dividendsPaid;

    if (reinvestDividends && sharePrice > 0) {
      const newSharesBought = dividendsPaid / sharePrice;
      shares += newSharesBought;
    }

    // Capital appreciation with overflow protection
    const newSharePrice = sharePrice * (1 + sharePriceGrowthRate / 100);
    sharePrice = Number.isFinite(newSharePrice) && newSharePrice < Number.MAX_SAFE_INTEGER 
      ? newSharePrice 
      : sharePrice; // Stop growing if overflow would occur
    
    // Dividend per share growth with overflow protection
    const newDividendPerShare = dividendPerShare * (1 + dividendGrowthRate / 100);
    dividendPerShare = Number.isFinite(newDividendPerShare) && newDividendPerShare < Number.MAX_SAFE_INTEGER 
      ? newDividendPerShare 
      : dividendPerShare; // Stop growing if overflow would occur

    const balance = shares * sharePrice;
    
    // Check if balance itself would overflow
    if (!Number.isFinite(balance) || balance > Number.MAX_SAFE_INTEGER) {
      // Cap the values and stop further growth
      break;
    }

    yearlyBreakdown.push({
      year,
      shares: roundToDecimal(shares, 4),
      sharePrice: roundToDecimal(sharePrice, 2),
      dividends: roundToDecimal(dividendsPaid, 2),
      balance: roundToDecimal(balance, 2),
    });
  }

  const endBalance = shares * sharePrice;

  return {
    endBalance: roundToDecimal(endBalance, 2),
    totalDividendsPaid: roundToDecimal(totalDividendsPaid, 2),
    yearlyBreakdown,
  };
}

export function ruleOf72(rate: number): { years: number } {
  if (rate <= 0) return { years: 0 };
  return { years: roundToDecimal(72 / rate, 1) };
}

export interface DcaItem {
  date: string;
  price: number;
  amountInvested: number;
  sharesBought: number;
  totalShares: number;
  totalInvested: number;
  portfolioValue: number;
}

/**
 * Dollar Cost Averaging (DCA) Simulation
 */
export function dollarCostAverage(
  periodicBudget: number,
  prices: number[]
): { totalInvested: number; totalSharesBought: number; avgCostPerShare: number; endPortfolioValue: number; returnPercent: number; dcaLog: DcaItem[] } {
  let totalInvested = 0;
  let totalShares = 0;
  const dcaLog: DcaItem[] = [];

  prices.forEach((price, index) => {
    totalInvested += periodicBudget;
    const sharesBought = periodicBudget / price;
    totalShares += sharesBought;
    const portfolioValue = totalShares * price;

    dcaLog.push({
      date: `Month ${index + 1}`,
      price: roundToDecimal(price, 2),
      amountInvested: periodicBudget,
      sharesBought: roundToDecimal(sharesBought, 4),
      totalShares: roundToDecimal(totalShares, 4),
      totalInvested: roundToDecimal(totalInvested, 2),
      portfolioValue: roundToDecimal(portfolioValue, 2),
    });
  });

  const avgCostPerShare = totalShares > 0 ? totalInvested / totalShares : 0;
  const endPrice = prices[prices.length - 1] || 0;
  const endPortfolioValue = totalShares * endPrice;
  const returnPercent = totalInvested > 0 ? ((endPortfolioValue - totalInvested) / totalInvested) * 100 : 0;

  return {
    totalInvested: roundToDecimal(totalInvested, 2),
    totalSharesBought: roundToDecimal(totalShares, 4),
    avgCostPerShare: roundToDecimal(avgCostPerShare, 2),
    endPortfolioValue: roundToDecimal(endPortfolioValue, 2),
    returnPercent: roundToDecimal(returnPercent, 2),
    dcaLog,
  };
}
export function calculateCAGR(beginValue: number, endValue: number, years: number): { cagr: number } {
  const rate = cagr(beginValue, endValue, years);
  return { cagr: rate * 100 }; // Convert to percentage
}

/**
 * US IRS August 2026 AFR Intra-Family Loan & Imputed Interest Calculator
 * Official Sourced Rates: Revenue Ruling RR-2026-13 (August 2026 Applicable Federal Rates).
 * Short-Term (≤3 yrs): 4.10% Annual (4.03% Monthly).
 * Mid-Term (>3 to 9 yrs): 4.35% Annual (4.26% Monthly).
 * Long-Term (>9 yrs): 4.92% Annual (4.81% Monthly).
 * IRC §7872(c)(2) $10,000 De Minimis Exception for gift loans between individuals.
 */
export function calculateIrsAfrIntraFamilyLoan(
  loanAmount: number = 100000,
  loanTermYears: number = 5,
  interestRateCharged: number = 3.0,
  compoundingFrequency: "annual" | "monthly" = "annual",
  totalFamilyLoans?: number
) {
  // August 2026 AFR Table (RR-2026-13)
  const afrTable = {
    shortTerm: compoundingFrequency === "monthly" ? 4.03 : 4.10,
    midTerm: compoundingFrequency === "monthly" ? 4.26 : 4.35,
    longTerm: compoundingFrequency === "monthly" ? 4.81 : 4.92
  };

  let termCategory = "Mid-Term (>3 to 9 Years)";
  let applicableAfrRate = afrTable.midTerm;

  if ((loanTermYears || 5) <= 3) {
    termCategory = "Short-Term (≤3 Years)";
    applicableAfrRate = afrTable.shortTerm;
  } else if ((loanTermYears || 5) > 9) {
    termCategory = "Long-Term (>9 Years)";
    applicableAfrRate = afrTable.longTerm;
  }

  const totalAggregateLoans = typeof totalFamilyLoans === "number" ? totalFamilyLoans : (loanAmount || 0);
  const isDeMinimisExempt = totalAggregateLoans <= 10000;
  const rateDeficit = Math.max(0, applicableAfrRate - (interestRateCharged || 0));
  const isBelowAfr = rateDeficit > 0 && !isDeMinimisExempt;

  const annualInterestCharged = (loanAmount || 0) * ((interestRateCharged || 0) / 100);
  const annualRequiredAfrInterest = (loanAmount || 0) * (applicableAfrRate / 100);
  
  const annualImputedInterest = isDeMinimisExempt ? 0 : (loanAmount || 0) * (rateDeficit / 100);
  const totalImputedInterestTerm = annualImputedInterest * (loanTermYears || 5);
  const totalInterestPaidByBorrower = annualInterestCharged * (loanTermYears || 5);

  let giftTaxRiskStatus = "AFR Compliant — Zero Imputed Interest Gift Tax Risk";
  let taxNote = "Interest rate charged equals or exceeds the August 2026 IRS AFR statutory minimum. No imputed interest or gift tax reporting required under IRC §7872.";

  if (isDeMinimisExempt) {
    giftTaxRiskStatus = "De Minimis Exempt — Zero Imputed Interest Risk ($10,000 threshold)";
    taxNote = "Under IRC §7872(c)(2), aggregate family loans of $10,000 or less between individuals are exempt from imputed interest rules (provided proceeds are not directly used to purchase income-producing assets).";
  } else if (isBelowAfr) {
    giftTaxRiskStatus = "Below-Market Loan — Imputed Interest Applies ($" + Math.round(annualImputedInterest).toLocaleString() + "/yr)";
    taxNote = "Under IRC §7872, charging below the August 2026 AFR creates $" + Math.round(annualImputedInterest).toLocaleString() + " of annual imputed interest. The lender is deemed to make a taxable gift to the borrower and must report taxable interest income.";
  }

  return {
    loanAmount: Math.round(loanAmount),
    loanTermYears: loanTermYears || 5,
    termCategory,
    applicableAfrRate: Number(applicableAfrRate.toFixed(2)),
    interestRateCharged: interestRateCharged || 0,
    annualInterestCharged: Math.round(annualInterestCharged),
    annualFamilyInterest: Math.round(annualInterestCharged),
    annualRequiredAfrInterest: Math.round(annualRequiredAfrInterest),
    annualAfrInterest: Math.round(annualRequiredAfrInterest),
    annualImputedInterest: Math.round(annualImputedInterest),
    annualImputedGift: Math.round(annualImputedInterest),
    totalImputedInterestTerm: Math.round(totalImputedInterestTerm),
    totalImputedGiftOverTerm: Math.round(totalImputedInterestTerm),
    totalInterestPaidByBorrower: Math.round(totalInterestPaidByBorrower),
    isBelowAfr,
    isDeMinimisExempt,
    giftTaxRiskStatus,
    taxNote,
    revRulingNotice: "IRS Revenue Ruling RR-2026-13 (August 2026 Applicable Federal Rates)"
  };
}

